import { ArrowLeftIcon, CheckIcon, XMarkIcon } from '@heroicons/react/16/solid';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Textarea } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import type {
  tHandoffReasonSchema,
  tLocaleLabel,
  tReasonOption,
} from './handoffReasonOptions.types';
import type { tHandoffReasonModalProps } from './HandoffReasonModal.types';
import './HandoffReasonModal.styles.scss';

// Duration the success state is visible before the modal closes. Long
// enough that the operator reads "thanks" + sees the checkmark land,
// short enough that it doesn't feel laggy. Exposed as a constant so the
// test file can match it (vi.advanceTimersByTime in fake-timer specs).
const SUCCESS_DISMISS_MS = 1800;

// V2.1 (RET-1069): schema-driven modal with inline multi-language labels.
// Every option/title/subtitle/subQuestion/q2Label string is a tLocaleLabel
// (`{ es, en, br }`) embedded in the schema — no i18n key indirection —
// so Producto can edit the PostHog flag payload to add/remove/rename
// options across all locales without a code deploy. Modal chrome strings
// (Skip/Confirm/Close/Back/textareaPlaceholder) stay as i18n keys because
// they're product-wide chrome, not surface content.

const RADIO_NAME_STEP1 = 'handoff-reason-step1';
const RADIO_NAME_STEP2 = 'handoff-reason-step2';

type tHandoffLocale = 'es' | 'en' | 'br';

// Normalize i18next's language code to our 3-locale bucket. i18n.language
// may be a region-tagged value ('es-MX', 'en-US', 'pt-BR') depending on
// the user's browser — we collapse to the 2-char prefix and default to
// Spanish (LeadSales' primary locale) for anything unrecognized.
function resolveLocale(language: string | undefined): tHandoffLocale {
  if (!language) return 'es';
  const lower = language.toLowerCase();
  if (lower.startsWith('en')) return 'en';
  if (lower.startsWith('br') || lower.startsWith('pt')) return 'br';
  return 'es';
}

// Pick the localized string from a tLocaleLabel, falling back to Spanish
// when the requested locale is missing. Producto can ship an option with
// only `es` filled in and the modal still renders without crashing.
function resolveLabel(
  label: tLocaleLabel,
  locale: tHandoffLocale,
): string {
  return label[locale] ?? label.es;
}

export default function HandoffReasonModal(props: tHandoffReasonModalProps) {
  if (!props.open || !props.schema) return null;
  return <HandoffReasonModalContent {...props} schema={props.schema} />;
}

type tContentProps = tHandoffReasonModalProps & {
  schema: tHandoffReasonSchema;
};

function HandoffReasonModalContent(props: tContentProps) {
  const { t, i18n } = useTranslation('chat/chat');
  const { schema, placement = 'bottom-right' } = props;

  const locale = useMemo(() => resolveLocale(i18n.language), [i18n.language]);

  const [selectedParent, setSelectedParent] = useState<tReasonOption | null>(
    null,
  );
  const [selectedSub, setSelectedSub] = useState<tReasonOption | null>(null);
  const [reasonText, setReasonText] = useState('');
  // 'idle'      → operator is filling out the form
  // 'submitted' → Confirm was clicked, showing thank-you state for
  //               SUCCESS_DISMISS_MS before the parent gets onConfirm
  //               (which closes the modal). Buys the operator visible
  //               proof their feedback registered before the surface
  //               disappears.
  const [submissionState, setSubmissionState] = useState<'idle' | 'submitted'>(
    'idle',
  );

  // Step 2 shows when a parent with sub-options is selected. A parent
  // without sub-options (e.g. "Otra razón") stays on Step 1 and reveals
  // its inline textarea there.
  const hasSubOptions = useMemo(
    () => (selectedParent?.subOptions?.length ?? 0) > 0,
    [selectedParent],
  );
  const onStep2 = selectedParent !== null && hasSubOptions;

  // Q2 textarea visibility:
  //   - On Step 1 (nested or flat): shown when the picked parent leaf
  //     opts in (`showTextarea`) — typically the "Otra razón" path.
  //   - On Step 2 (nested): shown when the schema defines a Q2 label.
  const isTextareaVisible = useMemo(() => {
    if (onStep2) return Boolean(schema.q2Label);
    return Boolean(selectedParent?.showTextarea);
  }, [onStep2, schema.q2Label, selectedParent]);

  // Confirm enabled when the operator has reached a leaf:
  //   - Step 1 + parent has no sub-options → ready
  //   - Step 2 + a sub-option is selected → ready
  const confirmEnabled = useMemo(() => {
    if (selectedParent === null) return false;
    if (!hasSubOptions) return true;
    return selectedSub !== null;
  }, [selectedParent, hasSubOptions, selectedSub]);

  const handleParentSelect = useCallback((option: tReasonOption) => {
    setSelectedParent(option);
    setSelectedSub(null);
    // Don't clear reasonText: an operator who picked Otra razón, typed,
    // then went back and chose another parent shouldn't lose their note.
  }, []);

  const handleSubSelect = useCallback((option: tReasonOption) => {
    setSelectedSub(option);
  }, []);

  const handleBack = useCallback(() => {
    setSelectedSub(null);
    setSelectedParent(null);
  }, []);

  // Tracks the post-confirm timeout so we can clear it if the modal
  // unmounts mid-animation (avoids "setState on unmounted" warnings and
  // ensures the parent's onConfirm fires exactly once).
  const successTimeoutRef = useRef<number | null>(null);

  const handleConfirm = useCallback(() => {
    if (!confirmEnabled || selectedParent === null) return;
    // Guard against double-click — once we're in the success animation,
    // further clicks are ignored. The button is also disabled visually
    // below but this is the authoritative state guard.
    if (submissionState !== 'idle') return;

    const trimmed = reasonText.trim();
    const reason_text =
      isTextareaVisible && trimmed.length > 0 ? trimmed : undefined;
    const payload = {
      reason_category: selectedParent.category,
      reason_subcategory: selectedSub?.category,
      reason_text,
    };

    setSubmissionState('submitted');
    // Hold the success state for SUCCESS_DISMISS_MS so the operator
    // visibly registers their submission, then fire onConfirm (parent
    // closes the modal). The analytics SUBMITTED event still fires
    // immediately inside onConfirm — operators don't bail before it.
    successTimeoutRef.current = window.setTimeout(() => {
      props.onConfirm(payload);
    }, SUCCESS_DISMISS_MS);
  }, [
    confirmEnabled,
    selectedParent,
    selectedSub,
    reasonText,
    isTextareaVisible,
    props,
    submissionState,
  ]);

  // Cleanup: cancel the pending close if the modal unmounts during the
  // success animation. Without this, an external close (e.g., parent
  // re-renders without `open`) would leak a setTimeout that calls
  // props.onConfirm post-unmount.
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current !== null) {
        window.clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  // ESC dismissal — optional surfaces only. Mandatory surfaces ignore
  // ESC; their only soft escape hatch is click-outside. Suspended once
  // submission lands so the operator can't ESC out of the thank-you
  // animation (which would skip the visual confirmation moment).
  useEffect(() => {
    if (schema.mandatory) return undefined;
    if (submissionState === 'submitted') return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') props.onClose('escape');
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [schema.mandatory, submissionState, props.onClose]);

  // Click-outside dismissal. Fires on both mandatory and optional
  // surfaces — distinguished only by which other dismissal paths exist.
  // The setTimeout(0) defers attaching the listener by one tick so the
  // mousedown that opened the modal (e.g. clicking "Desactivar" in the
  // ConfigHeader menu) doesn't immediately fire outside_click on us.
  // Suspended during the success animation — operators don't get to
  // cancel the visual confirmation by clicking out.
  const modalRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (submissionState === 'submitted') return undefined;
    const onMouseDown = (e: MouseEvent) => {
      if (!modalRef.current) return;
      if (!modalRef.current.contains(e.target as Node)) {
        props.onClose('outside_click');
      }
    };
    const armId = window.setTimeout(() => {
      document.addEventListener('mousedown', onMouseDown);
    }, 0);
    return () => {
      window.clearTimeout(armId);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [submissionState, props.onClose]);

  const titleText = onStep2 && schema.subQuestion
    ? resolveLabel(schema.subQuestion, locale)
    : resolveLabel(schema.q1Title, locale);

  // Portal to body so the modal lives outside the consumer tree's
  // flex/overflow context. createPortal makes the modal a direct child
  // of body — surrounding layout rules can't reach it.
  //
  // Backdrop renders BEFORE the modal so it sits below in z-order, but
  // only for mandatory variants — the per-chat optional pill stays
  // backdrop-free by design (non-blocking while the operator messages).
  // Clicks on the backdrop are "outside" the modal element, so the
  // existing document-mousedown listener fires onClose('outside_click')
  // without needing a separate backdrop click handler.
  return createPortal(
    <>
      {schema.mandatory && (
        <div className="HandoffReasonModal__Backdrop" aria-hidden="true" />
      )}
      <div
        ref={modalRef}
        className={classNames('HandoffReasonModal', {
          'HandoffReasonModal--center': placement === 'center',
          'HandoffReasonModal--mandatory': schema.mandatory,
          'HandoffReasonModal--submitted': submissionState === 'submitted',
        })}
        role="dialog"
        aria-modal={schema.mandatory ? 'true' : 'false'}
        aria-label={titleText}
      >
      <AnimatePresence mode="wait">
        {submissionState === 'submitted' ? (
          <motion.div
            key="success"
            className="HandoffReasonModal__Success"
            // The form content fades out; the success state fades in
            // slightly delayed for a smooth handoff. The whole thing
            // sits inside the same modal frame (no jump, same anchor).
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            role="status"
            aria-live="polite"
          >
            <motion.div
              className="HandoffReasonModal__SuccessIcon"
              // Spring scale gives the checkmark a soft bounce-in. Slight
              // overshoot reads as "landed with weight" — more confidence
              // than a flat linear fade.
              initial={{ scale: 0, rotate: -12 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{
                type: 'spring',
                stiffness: 320,
                damping: 18,
                delay: 0.05,
              }}
              aria-hidden="true"
            >
              <CheckIcon className="HandoffReasonModal__SuccessIconGlyph" />
            </motion.div>
            <motion.h3
              className="HandoffReasonModal__SuccessTitle"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.24, delay: 0.18 }}
            >
              {t('smartLeadbot.handoffReason.thanks.title')}
            </motion.h3>
            <motion.p
              className="HandoffReasonModal__SuccessSubtitle"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.24, delay: 0.26 }}
            >
              {t('smartLeadbot.handoffReason.thanks.subtitle')}
            </motion.p>
          </motion.div>
        ) : (
          <motion.div
            key="form"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
            className="HandoffReasonModal__FormContent"
          >
      <div className="HandoffReasonModal__Header">
        <div className="HandoffReasonModal__TitleGroup">
          {onStep2 && (
            <Button
              color="ghost"
              size="sm"
              shape="square"
              aria-label={t('smartLeadbot.handoffReason.back')}
              onClick={handleBack}
              className="HandoffReasonModal__BackButton"
            >
              <ArrowLeftIcon className="HandoffReasonModal__BackIcon" />
            </Button>
          )}
          <div className="HandoffReasonModal__TitleStack">
            <h3 className="HandoffReasonModal__Title">{titleText}</h3>
            {!onStep2 && schema.q1Subtitle && (
              <p className="HandoffReasonModal__Subtitle">
                {resolveLabel(schema.q1Subtitle, locale)}
              </p>
            )}
          </div>
        </div>
        {!schema.mandatory && (
          <Button
            color="ghost"
            size="sm"
            shape="square"
            aria-label={t('smartLeadbot.handoffReason.closeAriaLabel')}
            onClick={() => props.onClose('close_button')}
          >
            <XMarkIcon className="HandoffReasonModal__CloseIcon" />
          </Button>
        )}
      </div>

      {onStep2 ? (
        <fieldset className="HandoffReasonModal__Options" role="radiogroup">
          {selectedParent!.subOptions!.map((sub) => {
            const radioId = `handoff-reason-sub-${sub.category}`;
            const isSelected = selectedSub?.category === sub.category;
            return (
              <label
                key={sub.category}
                htmlFor={radioId}
                className={classNames('HandoffReasonModal__Option', {
                  'HandoffReasonModal__Option--selected': isSelected,
                })}
              >
                {/* Native input keeps a11y semantics + keyboard navigation;
                    the visual chrome is the card + check icon. */}
                <input
                  id={radioId}
                  type="radio"
                  name={RADIO_NAME_STEP2}
                  className="sr-only"
                  checked={isSelected}
                  onChange={() => handleSubSelect(sub)}
                />
                <span className="HandoffReasonModal__OptionLabel">
                  {resolveLabel(sub.labels, locale)}
                </span>
                {isSelected && (
                  <CheckIcon
                    className="HandoffReasonModal__OptionCheck"
                    aria-hidden="true"
                  />
                )}
              </label>
            );
          })}
        </fieldset>
      ) : (
        <fieldset className="HandoffReasonModal__Options" role="radiogroup">
          {schema.options.map((option) => {
            const radioId = `handoff-reason-${option.category}`;
            const isSelected = selectedParent?.category === option.category;
            return (
              <label
                key={option.category}
                htmlFor={radioId}
                className={classNames('HandoffReasonModal__Option', {
                  'HandoffReasonModal__Option--selected': isSelected,
                })}
              >
                <input
                  id={radioId}
                  type="radio"
                  name={RADIO_NAME_STEP1}
                  className="sr-only"
                  checked={isSelected}
                  onChange={() => handleParentSelect(option)}
                />
                <span className="HandoffReasonModal__OptionLabel">
                  {resolveLabel(option.labels, locale)}
                </span>
                {isSelected && (
                  <CheckIcon
                    className="HandoffReasonModal__OptionCheck"
                    aria-hidden="true"
                  />
                )}
              </label>
            );
          })}
        </fieldset>
      )}

      {isTextareaVisible && (
        <div className="HandoffReasonModal__TextareaGroup">
          {schema.q2Label && onStep2 && (
            <label className="HandoffReasonModal__TextareaLabel">
              {resolveLabel(schema.q2Label, locale)}
            </label>
          )}
          <Textarea
            className="HandoffReasonModal__Textarea"
            placeholder={t('smartLeadbot.handoffReason.textareaPlaceholder')}
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
            maxLength={500}
          />
        </div>
      )}

      <div className="HandoffReasonModal__Actions">
        {!schema.mandatory && props.onSkip && (
          <Button color="ghost" size="xs" onClick={props.onSkip}>
            {t('smartLeadbot.handoffReason.skip')}
          </Button>
        )}
        <Button
          color="primary"
          size="xs"
          disabled={!confirmEnabled}
          onClick={handleConfirm}
        >
          {t('smartLeadbot.handoffReason.confirm')}
        </Button>
      </div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </>,
    document.body,
  );
}
